import { Vector3, type CameraView } from '@/math'

import { parseKeyX, parseKeyY, parseKeyZ, Position, steppersFromAxisUnit } from '../voxelMath'

import type { VoxelMap, VoxelData, Face } from '../voxelTypes'
import { projectPointerToLineScalar } from '@/core/project'
import { faceToNormal } from '../voxelLiterals'
import { consistentNormalUnit } from '@/core/axis'
import { findContiguousFaceByType } from './faceHelpers'
import { writeFill, writePlace, writeSubtract } from '@/events/layerSystem'

export interface ExtrusionState {
  faceKeys: Set<number>
  facePositions: Set<Position>
  faceVoxels: Map<number, VoxelData>
  normal: Vector3
  axisUnit: { dx: number; dy: number; dz: number }
  startPos: Position
  // Current offset along normal (integer steps)
  offset: number
  minOffset: number
  maxOffset: number
}


export function calculateExtrusionOffset(
  ev: PointerEvent,
  extrusionState: ExtrusionState,
  camera: CameraView,
  canvas: HTMLCanvasElement
): number {
  const lineOrigin = new Vector3(
    extrusionState.startPos.x + 0.5,
    extrusionState.startPos.y + 0.5,
    extrusionState.startPos.z + 0.5
  )
  const s = projectPointerToLineScalar(ev, camera, canvas.getBoundingClientRect(), lineOrigin, extrusionState.normal)
  // Snap to nearest integer step (no clamping; clipping handled downstream)
  return Math.round(s)
}

// ============================================================================
// New stateless helpers (anchor + face + params) for previews/commits
// ============================================================================

export function surfaceKeys(
  anchor: Position,
  face: Face,
  grid: VoxelMap,
  _sameTypeAndColor = true,
): Set<number> {
  const n = faceToNormal(face)
  const normal = new Vector3(n.x, n.y, n.z)
  // Simplified default: contiguous by type (ignore color)
  return findContiguousFaceByType(anchor, normal, grid)
}


// Key-based variants to avoid intermediate Position objects
// generateExtrusionKeyCommandsFromFace removed; use applyExtrusionFromFace instead.

export function generateExtrusionKeyCommandsFromKeys(
  keys: Set<number>,
  face: Face,
  _grid: VoxelMap,
  offset: number,
  bounds: Position
): { removalKeys?: Set<number>, placementPairs?: Array<{ dstKey: number, srcKey: number }>, usedOffset: number } {
  if (offset === 0) return { usedOffset: 0 }
  const n = faceToNormal(face)
  const axisUnit = consistentNormalUnit(n)
  const clamped = clampOffsetToBounds(keys, axisUnit, offset, bounds)
  if (clamped === 0) return { usedOffset: 0 }
  // Choose axis-specific steppers once
  const { stepPos, stepNeg } = steppersFromAxisUnit(axisUnit)
  if (clamped < 0) {
    const ks = new Set<number>()
    const steps = -clamped
    for (const key of keys) {
      let k = key
      // Include starting position, then step toward negative direction (opposite of axisUnit)
      ks.add(k)
      for (let i = 1; i < steps; i++) { k = stepNeg(k); ks.add(k) }
    }
    return { removalKeys: ks, usedOffset: clamped }
  } else {
    const pairs: Array<{ dstKey: number, srcKey: number }> = []
    for (const key of keys) {
      let k = key
      for (let i = 1; i <= clamped; i++) { k = stepPos(k); pairs.push({ dstKey: k, srcKey: key }) }
    }
    return { placementPairs: pairs, usedOffset: clamped }
  }
}

// ============================================================================
// Apply-on-layer helpers (mutate target directly)
// ============================================================================

export function applyExtrusionFromKeys(
  target: VoxelMap,
  source: VoxelMap,
  keys: Set<number>,
  face: Face,
  offset: number,
  bounds: Position,
  mode: 'place' | 'fill' = 'place'
): { usedOffset: number } {
  if (offset === 0) return { usedOffset: 0 }
  const n = faceToNormal(face)
  const axisUnit = consistentNormalUnit(n)
  const clamped = clampOffsetToBounds(keys, axisUnit, offset, bounds)
  if (clamped === 0) return { usedOffset: 0 }

  const { stepPos, stepNeg } = steppersFromAxisUnit(axisUnit)

  if (clamped < 0) {
    const steps = -clamped
    for (const key of keys) {
      let k = key
      // Include starting position, then step negatively (opposite axisUnit)
      writeSubtract(target as any, k)
      for (let i = 1; i < steps; i++) { k = stepNeg(k); writeSubtract(target as any, k) }
    }
    return { usedOffset: clamped }
  } else {
    const write = mode === 'fill' ? (k: number, v: VoxelData) => writeFill(target as any, k, v) : (k: number, v: VoxelData) => writePlace(target as any, k, v)
    for (const key of keys) {
      const v = source.get(key)
      if (!v) continue
      let k = key
      for (let i = 1; i <= clamped; i++) { k = stepPos(k); write(k, v) }
    }
    return { usedOffset: clamped }
  }
}

export function applyExtrusionFromFace(
  target: VoxelMap,
  source: VoxelMap,
  anchor: Position,
  face: Face,
  offset: number,
  bounds: Position,
  sameTypeAndColor: boolean = true,
  mode: 'place' | 'fill' = 'place'
): { usedOffset: number } {
  const surface = surfaceKeys(anchor, face, source, sameTypeAndColor)
  return applyExtrusionFromKeys(target, source, surface, face, offset, bounds, mode)
}

export function advanceAnchor(anchor: Position, face: Face, offset: number): Position {
  const n = faceToNormal(face)
  const dx = Math.sign(n.x) | 0
  const dy = Math.sign(n.y) | 0
  const dz = Math.sign(n.z) | 0
  return { x: anchor.x + dx * offset, y: anchor.y + dy * offset, z: anchor.z + dz * offset }
}

// Compute clamped offset so that no stepped position leaves bounds for the surface
function clampOffsetToBounds(
  keys: Set<number>,
  axisUnit: { dx: number; dy: number; dz: number },
  offset: number,
  bounds: Position
): number {
  if (offset === 0) return 0
  // Compute max positive and negative steps allowed for the entire surface
  let maxPos = Infinity
  let maxNeg = Infinity
  // Identify primary stepping axis (exactly one of dx,dy,dz is non-zero)
  const ax = axisUnit.dx !== 0 ? 'x' : (axisUnit.dy !== 0 ? 'y' : 'z')

  for (const key of keys) {
    const x = parseKeyX(key)
    const y = parseKeyY(key)
    const z = parseKeyZ(key)
    // Distance to bounds in positive axisUnit direction
    if (ax === 'x') {
      const posSteps = axisUnit.dx > 0 ? (bounds.x - 1 - x) : x
      const negSteps = axisUnit.dx > 0 ? x : (bounds.x - 1 - x)
      maxPos = Math.min(maxPos, posSteps)
      maxNeg = Math.min(maxNeg, negSteps)
    } else if (ax === 'y') {
      const posSteps = axisUnit.dy > 0 ? (bounds.y - 1 - y) : y
      const negSteps = axisUnit.dy > 0 ? y : (bounds.y - 1 - y)
      maxPos = Math.min(maxPos, posSteps)
      maxNeg = Math.min(maxNeg, negSteps)
    } else {
      const posSteps = axisUnit.dz > 0 ? (bounds.z - 1 - z) : z
      const negSteps = axisUnit.dz > 0 ? z : (bounds.z - 1 - z)
      maxPos = Math.min(maxPos, posSteps)
      maxNeg = Math.min(maxNeg, negSteps)
    }
  }
  if (offset > 0) return Math.max(0, Math.min(offset, maxPos | 0))
  // offset < 0
  const allowedNeg = Math.max(0, maxNeg | 0)
  if (-offset > allowedNeg) {
    // Use -(allowedNeg + 1) so that the exclusive loop includes the boundary layer
    return -(allowedNeg + 1)
  }
  return Math.min(0, offset)
}
